import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import React, {useCallback, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import {Box, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Button from "../../base/button/button";
import Form, {MuiRFF, Yup} from '../../form';
import Grid2 from "@mui/material/Unstable_Grid2";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import useAccessLevels from "../../../hooks/use-access-levels";
import {useTranslation} from "react-i18next";

/**
 * Add / update vendor contact user via CleanAF Vendor user endpoints.
 */
const UpsertVendorMemberDialog = ({open, close, member, vendorId, onSuccess}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendor-account-info.memberDialog'});
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canManageAccountInfo = packages.base.manageVendorAccountInfo;

    const schema = useMemo(() => Yup.object().shape({
        firstName: Yup.string().nullable().required(ValidateMessages.required),
        lastName: Yup.string().nullable().required(ValidateMessages.required),
        email: Yup.string().email(ValidateMessages.incorrect).nullable().required(ValidateMessages.required),
        role: Yup.string().trim().nullable(),
        phone: Yup.string().trim().nullable().required(ValidateMessages.required),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        firstName: member?.firstname ?? member?.firstName ?? '',
        lastName: member?.lastname ?? member?.lastName ?? '',
        email: member?.email ?? '',
        role: member?.role ?? member?.position?.title ?? member?.position ?? '',
        phone: member?.phone ?? '',
    }), [member]);

    /**
     * Sends AddUser / UpdateUser with Postman firstname casing and updatedProperties.
     */
    const upsertVendor = useCallback(async (values) => {
        if (!canManageAccountInfo) return;
        setSubmitting(true);
        const firstname = values.firstName;
        const lastname = values.lastName;
        const email = values.email;
        const phone = values.phone;
        const role = values.role ?? '';

        let response;
        if (member?.id) {
            const prevFirst = member.firstname ?? member.firstName ?? '';
            const prevLast = member.lastname ?? member.lastName ?? '';
            const prevEmail = member.email ?? '';
            const prevPhone = member.phone ?? '';
            const prevRole = member.role ?? member.position?.title ?? member.position ?? '';

            const updatedProperties = [];
            if (firstname !== prevFirst)
                updatedProperties.push('Firstname');
            if (lastname !== prevLast)
                updatedProperties.push('Lastname');
            if (email !== prevEmail)
                updatedProperties.push('Email');
            if (phone !== prevPhone)
                updatedProperties.push('Phone');
            if (role !== prevRole)
                updatedProperties.push('Role');

            if (!updatedProperties.length) {
                setSubmitting(false);
                close();
                return;
            }

            response = await Api.updateVendorUser({
                id: member.id,
                firstname,
                lastname,
                email,
                phone,
                role,
                updatedProperties,
            });
        } else {
            response = await Api.addVendorUser({
                vendorId: Number(vendorId),
                firstname,
                lastname,
                email,
                phone,
                role,
            });
        }
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onSuccess?.(response.data);
            close();
        }
    }, [canManageAccountInfo, close, isMounted, member, onSuccess, vendorId]);

    return (
        <Dialog open={open} setOpen={close} maxWidth={'sm'}>
            <DialogTitle>
                {member ? t('editTitle') : t('addTitle')}
            </DialogTitle>
            <DialogContent dividers>
                <Form
                    onSubmit={upsertVendor}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{id: 'upsert-vendor-member-form'}}
                >
                    {() => (
                        <Grid2 container spacing={2}>
                            <Grid2 xs={12} md={6}>
                                <MuiRFF.TextField
                                    name={'firstName'}
                                    label={t('firstName')}
                                    required={required.firstName}
                                    fullWidth
                                    disabled={submitting}
                                />
                            </Grid2>
                            <Grid2 xs={12} md={6}>
                                <MuiRFF.TextField
                                    name={'lastName'}
                                    label={t('lastName')}
                                    required={required.lastName}
                                    fullWidth
                                    disabled={submitting}
                                />
                            </Grid2>
                            <Grid2 xs={12}>
                                <MuiRFF.TextField
                                    name={'email'}
                                    label={t('email')}
                                    required={required.email}
                                    fullWidth
                                    disabled={submitting}
                                />
                            </Grid2>
                            <Grid2 xs={12} md={6}>
                                <MuiRFF.TextField
                                    name={'phone'}
                                    label={t('phone')}
                                    required={required.phone}
                                    fullWidth
                                    disabled={submitting}
                                />
                            </Grid2>
                            <Grid2 xs={12} md={6}>
                                <MuiRFF.TextField
                                    name={'role'}
                                    label={t('role')}
                                    fullWidth
                                    disabled={submitting}
                                />
                            </Grid2>
                        </Grid2>
                    )}
                </Form>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={close}
                        borderRadius={'0 8px 0 8px'}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        type={'submit'}
                        form={'upsert-vendor-member-form'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {submitting
                            ? (member ? t('updating') : t('creating'))
                            : (member ? t('update') : t('create'))}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default UpsertVendorMemberDialog;
